import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
  Center,
  Flex,
  Heading,
  Icon,
  Input,
  Spinner,
  Stack,
  Tag,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FiArrowLeft, FiCheckCircle, FiDroplet } from 'react-icons/fi';
import { useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';

import { useCan } from '../../../hooks/useCan';
import {
  FuelReading,
  FuelSlot,
  fuelErrorMessage,
  fuelErrorStatus,
  fuelReadingsService,
} from '../../../services/fuelReadingsService';
import {
  SLOT_LABEL,
  formatDateTime,
  formatLiters,
  formatReading,
  hourLabel,
  isoDay,
  parseReading,
  slotFor,
  timesFor,
} from '../../../utils/fuelReadings';
import { CalibrationForm } from './CalibrationForm';
import { FuelShell } from './FuelShell';
import { MissingPhotos } from './MissingPhotos';
import { PhotoInput } from './PhotoInput';
import { ReceiptForm } from './ReceiptForm';

const PUMPS_KEY = ['fuel-readings', 'pumps'];
const LATEST_KEY = ['fuel-readings', 'latest'];

// tela do frentista, pensada para o celular na bomba: escolhe o turno e a
// bomba, digita o número do totalizador, tira a foto (ou anexa depois) e salva.
export default function NewFuelReading() {
  const queryClient = useQueryClient();
  const canCreate = useCan({ permissions: ['fuel_reading_create'] });
  const isAdmin = useCan({ permissions: ['user_adm'] });
  const canView = useCan({ permissions: ['fuel_reading_view'] });
  const allowed = canCreate || isAdmin;

  const pumps = useQuery(PUMPS_KEY, fuelReadingsService.pumps, {
    enabled: allowed,
  });
  const latest = useQuery(
    LATEST_KEY,
    () => fuelReadingsService.readings({ limit: 20 }),
    { enabled: allowed },
  );

  const [mode, setMode] = useState<'reading' | 'calibration' | 'receipt'>(
    'reading',
  );
  const [chosenSlot, setSlot] = useState<FuelSlot | null>(null);
  const [pumpId, setPumpId] = useState('');
  const [value, setValue] = useState('');
  const [photo, setPhoto] = useState<Blob | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [saved, setSaved] = useState<FuelReading | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const activePumps = useMemo(
    () => (pumps.data || []).filter(pump => pump.active),
    [pumps.data],
  );
  const pump = activePumps.find(item => item.id === pumpId);
  // horários de hoje (no sábado o posto fecha às 11h); o turno vem da hora
  // até o frentista escolher outro
  const times = timesFor(
    activePumps[0] || { expected_times: '07:00,17:00' },
    new Date(),
  );
  const slot = chosenSlot ?? slotFor(new Date(), times);

  // leituras válidas de hoje, por bomba e turno
  const today = isoDay(new Date());
  const doneToday = useMemo(() => {
    const done: Record<string, FuelReading> = {};

    (latest.data || [])
      .filter(item => !item.canceled_at && item.read_at.startsWith(today))
      .forEach(item => {
        done[`${item.pump_id}|${item.slot}`] = item;
      });

    return done;
  }, [latest.data, today]);
  const doneFor = (id: string, which: FuelSlot = slot) =>
    doneToday[`${id}|${which}`];

  // começa na primeira bomba que ainda falta no turno
  useEffect(() => {
    if (!pumpId && activePumps.length > 0 && latest.isFetched) {
      const pending = activePumps.find(
        item => !doneToday[`${item.id}|${slot}`],
      );

      setPumpId((pending || activePumps[0]).id);
    }
  }, [pumpId, activePumps, doneToday, slot, latest.isFetched]);

  const reading = parseReading(value);
  const previous = pump?.last_reading ?? null;
  const liters =
    Number.isFinite(reading) && previous !== null ? reading - previous : null;
  const lower = liters !== null && liters < 0;
  const alreadyDone = pump ? doneFor(pump.id) : undefined;

  // ajuda embaixo do número: compara com a leitura anterior da bomba
  const hint = (): [string, string] => {
    if (!pump || previous === null) {
      return ['Primeira leitura desta bomba.', 'gray.500'];
    }

    const last = pump.last_slot
      ? `${SLOT_LABEL[pump.last_slot].toLowerCase()} de ${formatDateTime(
          pump.last_read_at,
        )}`
      : 'anterior';
    const afterClosing = pump.last_slot === 'fechamento';

    if (liters === null) {
      return slot === 'abertura' && afterClosing
        ? [
            `Deve ser igual ao último fechamento: ${formatReading(previous)}`,
            'gray.500',
          ]
        : [
            `Leitura anterior (${last}): ${formatReading(previous)}`,
            'gray.500',
          ];
    }

    if (lower) {
      return [
        `Menor que a anterior (${formatReading(previous)}). Confira o número.`,
        'red.400',
      ];
    }

    // abertura acima do último fechamento: saiu diesel fora do expediente
    if (slot === 'abertura' && afterClosing) {
      return liters > 0.5
        ? [
            `${formatLiters(
              liters,
            )} L a mais que o último fechamento (${formatReading(
              previous,
            )}). Se a bomba foi usada fora do horário, conte na ocorrência.`,
            'orange.400',
          ]
        : ['Igual ao último fechamento.', 'green.400'];
    }

    return [
      `${formatLiters(liters)} L desde a leitura anterior (${last}).`,
      'gray.500',
    ];
  };
  const [hintText, hintColor] = hint();

  const reset = () => {
    setValue('');
    setPhoto(null);
    setNotes('');
    setError('');
  };

  const submit = async (confirm = false) => {
    if (!pump || !Number.isFinite(reading)) {
      setError('Escolha a bomba e digite o número do totalizador.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const result = await fuelReadingsService.createReading({
        pump_id: pump.id,
        slot,
        reading: value.trim(),
        photo,
        notes: notes.trim() || undefined,
        confirm_high_jump: confirm,
      });

      setConfirmMessage('');
      setSaved(result);
      reset();
      await queryClient.invalidateQueries('fuel-readings');
    } catch (err) {
      const message = fuelErrorMessage(
        err,
        'Não foi possível salvar a leitura.',
      );

      if (fuelErrorStatus(err) === 409) {
        setConfirmMessage(message);
      } else {
        setConfirmMessage('');
        setError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const nextPump = activePumps.find(
    item => item.id !== saved?.pump_id && !doneFor(item.id),
  );

  const goNext = () => {
    setSaved(null);
    if (nextPump) {
      setPumpId(nextPump.id);
    }
  };

  const chooseSlot = (which: FuelSlot) => {
    setSlot(which);
    setError('');
    const pending = activePumps.find(item => !doneToday[`${item.id}|${which}`]);

    if (pending) {
      setPumpId(pending.id);
    }
  };

  if (!allowed) {
    return (
      <FuelShell maxW="520px">
        <Alert status="warning" color="orange.900" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Você não tem permissão para lançar a leitura do encerrante.
          </AlertDescription>
        </Alert>
      </FuelShell>
    );
  }

  return (
    <FuelShell maxW="520px">
      <Flex align="center" justify="space-between" mb="4" gridGap="2">
        <Box>
          <Heading size="md">Posto de combustível</Heading>
          <Text fontSize="sm" color="gray.500">
            Diesel S10 · hoje leitura às {hourLabel(times[0])} e às{' '}
            {hourLabel(times[1])}
          </Text>
        </Box>
        {(canView || isAdmin) && (
          <Button
            as={Link}
            to="/fuel-readings"
            size="sm"
            variant="ghost"
            leftIcon={<FiArrowLeft />}
          >
            Painel
          </Button>
        )}
      </Flex>

      <ButtonGroup isAttached w="100%" mb="5">
        <Button
          flex="1"
          colorScheme={mode === 'reading' ? 'blue' : 'gray'}
          variant={mode === 'reading' ? 'solid' : 'outline'}
          onClick={() => setMode('reading')}
        >
          Leitura
        </Button>
        <Button
          flex="1"
          colorScheme={mode === 'calibration' ? 'blue' : 'gray'}
          variant={mode === 'calibration' ? 'solid' : 'outline'}
          onClick={() => setMode('calibration')}
        >
          Aferição
        </Button>
        <Button
          flex="1"
          colorScheme={mode === 'receipt' ? 'blue' : 'gray'}
          variant={mode === 'receipt' ? 'solid' : 'outline'}
          onClick={() => setMode('receipt')}
        >
          Recebimento
        </Button>
      </ButtonGroup>

      <MissingPhotos readings={latest.data || []} />

      {mode === 'receipt' && <ReceiptForm />}

      {pumps.isLoading && (
        <Center py="10">
          <Spinner size="lg" color="blue.500" />
        </Center>
      )}

      {pumps.isError && (
        <Alert status="error" color="red.900" borderRadius="md">
          <AlertIcon />
          <AlertDescription flex="1">
            Não foi possível carregar as bombas.
          </AlertDescription>
          <Button size="sm" onClick={() => pumps.refetch()}>
            Tentar de novo
          </Button>
        </Alert>
      )}

      {mode === 'calibration' && activePumps.length > 0 && (
        <CalibrationForm pumps={activePumps} />
      )}

      {mode === 'reading' && saved && (
        <Stack spacing="4">
          <Box
            bg="green.50"
            color="green.900"
            borderWidth="1px"
            borderColor="green.300"
            borderRadius="12px"
            p="5"
            textAlign="center"
          >
            <Icon as={FiCheckCircle} boxSize="12" color="green.500" />
            <Text fontWeight="bold" fontSize="lg" mt="2">
              Leitura de {SLOT_LABEL[saved.slot].toLowerCase()} registrada
            </Text>
            <Text>
              {saved.pump_name}: <b>{formatReading(saved.reading)}</b>
            </Text>
            {saved.slot === 'fechamento' && saved.liters !== null && (
              <Text fontSize="sm">
                {formatLiters(saved.liters)} L desde a abertura.
              </Text>
            )}
            <Text fontSize="sm" mt="1">
              {formatDateTime(saved.read_at, true)}
            </Text>
          </Box>
          {!!saved.overnight_liters && (
            <Alert status="warning" color="orange.900" borderRadius="md">
              <AlertIcon />
              <AlertDescription>
                A abertura ficou {formatLiters(saved.overnight_liters)} L acima
                do último fechamento. Isso aparece como ocorrência para a
                controladoria.
              </AlertDescription>
            </Alert>
          )}
          {nextPump ? (
            <Button colorScheme="blue" size="lg" onClick={goNext}>
              Lançar a {nextPump.name}
            </Button>
          ) : (
            <Text textAlign="center" color="green.400" fontWeight="medium">
              Todas as bombas já têm a leitura de{' '}
              {SLOT_LABEL[slot].toLowerCase()} de hoje.
            </Text>
          )}
          <Button size="lg" variant="outline" onClick={() => setSaved(null)}>
            Voltar
          </Button>
        </Stack>
      )}

      {mode === 'reading' && !saved && activePumps.length > 0 && (
        <Stack spacing="5">
          <Box>
            <Text fontWeight="medium" mb="2">
              1. Turno
            </Text>
            <ButtonGroup isAttached w="100%">
              {(['abertura', 'fechamento'] as FuelSlot[]).map(
                (which, index) => (
                  <Button
                    key={which}
                    flex="1"
                    size="lg"
                    colorScheme={slot === which ? 'blue' : 'gray'}
                    variant={slot === which ? 'solid' : 'outline'}
                    onClick={() => chooseSlot(which)}
                  >
                    {SLOT_LABEL[which]} ({hourLabel(times[index])})
                  </Button>
                ),
              )}
            </ButtonGroup>
          </Box>

          <Box>
            <Text fontWeight="medium" mb="2">
              2. Bomba
            </Text>
            <Stack spacing="2">
              {activePumps.map(item => {
                const selected = item.id === pumpId;
                const done = doneFor(item.id);

                return (
                  <Box
                    key={item.id}
                    as="button"
                    type="button"
                    onClick={() => setPumpId(item.id)}
                    textAlign="left"
                    p="4"
                    borderRadius="12px"
                    borderWidth="2px"
                    borderColor={selected ? 'blue.400' : 'gray.700'}
                    bg="gray.800"
                    aria-pressed={selected}
                  >
                    <Flex align="center" gridGap="3">
                      <Flex
                        w="10"
                        h="10"
                        borderRadius="full"
                        bg={selected ? 'blue.100' : 'gray.700'}
                        color={selected ? 'blue.600' : 'gray.400'}
                        align="center"
                        justify="center"
                        flexShrink={0}
                      >
                        <Icon as={FiDroplet} boxSize="5" />
                      </Flex>
                      <Box flex="1" minW="0">
                        <Text fontWeight="bold">{item.name}</Text>
                        <Text fontSize="sm" color="gray.500">
                          {item.last_reading === null
                            ? 'Ainda sem leitura'
                            : `Última: ${formatReading(
                                item.last_reading,
                              )} · ${formatDateTime(item.last_read_at)}`}
                        </Text>
                      </Box>
                      <Tag
                        size="sm"
                        colorScheme={done ? 'green' : 'orange'}
                        flexShrink={0}
                      >
                        {done ? `feita ${done.read_at.slice(11, 16)}` : 'falta'}
                      </Tag>
                    </Flex>
                  </Box>
                );
              })}
            </Stack>
          </Box>

          {pump && alreadyDone && (
            <Alert status="success" color="green.900" borderRadius="md">
              <AlertIcon />
              <AlertDescription>
                A leitura de {SLOT_LABEL[slot].toLowerCase()} de hoje da{' '}
                {pump.name} já foi lançada: {formatReading(alreadyDone.reading)}{' '}
                às {alreadyDone.read_at.slice(11, 16)}. Se estiver errada, peça
                para a controladoria anular.
              </AlertDescription>
            </Alert>
          )}

          {pump && !alreadyDone && (
            <>
              <Box>
                <Text fontWeight="medium" mb="2">
                  3. Número do totalizador
                </Text>
                <Input
                  value={value}
                  onChange={event => setValue(event.target.value)}
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder="Ex.: 30412,7"
                  size="lg"
                  fontSize="2xl"
                  textAlign="center"
                  fontFamily="mono"
                  h="16"
                  isInvalid={lower || (!!value && !Number.isFinite(reading))}
                  aria-label="Número do totalizador"
                />
                <Text fontSize="sm" mt="2" color={hintColor} textAlign="center">
                  {hintText}
                </Text>
              </Box>

              <Box>
                <Text fontWeight="medium" mb="2">
                  4. Foto do totalizador (opcional)
                </Text>
                <PhotoInput
                  label="Tirar foto do totalizador"
                  value={photo}
                  onChange={setPhoto}
                  onPreparing={setPreparing}
                />
              </Box>

              <Box>
                <Text fontSize="sm" mb="1" color="gray.500">
                  Ocorrência (opcional)
                </Text>
                <Textarea
                  value={notes}
                  onChange={event => setNotes(event.target.value)}
                  maxLength={500}
                  rows={2}
                  placeholder="Ex.: bomba travou, abastecimento fora do horário"
                  aria-label="Ocorrência"
                />
              </Box>

              {error && (
                <Alert status="error" color="red.900" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                colorScheme="blue"
                size="lg"
                h="14"
                onClick={() => submit(false)}
                isLoading={saving}
                loadingText="Salvando..."
                isDisabled={!Number.isFinite(reading) || lower || preparing}
              >
                Salvar leitura de {SLOT_LABEL[slot].toLowerCase()}
              </Button>
            </>
          )}

          {(latest.data || []).length > 0 && (
            <Box>
              <Text fontWeight="medium" mb="2" color="gray.500" fontSize="sm">
                Últimas leituras
              </Text>
              <Stack spacing="1">
                {(latest.data || []).slice(0, 6).map(item => (
                  <Flex
                    key={item.id}
                    justify="space-between"
                    fontSize="sm"
                    borderBottomWidth="1px"
                    borderColor="gray.700"
                    py="1"
                    opacity={item.canceled_at ? 0.5 : 1}
                    textDecoration={item.canceled_at ? 'line-through' : 'none'}
                  >
                    <Text>
                      {formatDateTime(item.read_at)} · {item.pump_name} ·{' '}
                      {SLOT_LABEL[item.slot].toLowerCase()}
                    </Text>
                    <Text fontFamily="mono">{formatReading(item.reading)}</Text>
                  </Flex>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      )}

      <AlertDialog
        isOpen={!!confirmMessage}
        leastDestructiveRef={cancelRef}
        onClose={() => setConfirmMessage('')}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="gray.700" mx="4">
            <AlertDialogHeader>Confirme o número</AlertDialogHeader>
            <AlertDialogBody>{confirmMessage}</AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                variant="ghost"
                onClick={() => setConfirmMessage('')}
              >
                Corrigir
              </Button>
              <Button
                colorScheme="orange"
                ml={3}
                isLoading={saving}
                onClick={() => submit(true)}
              >
                Está certo, salvar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </FuelShell>
  );
}
